import { Link } from "react-router-dom";
import { MapPin, ChevronRight, Phone } from "lucide-react";
import { usePageMeta } from "./usePageMeta";
import { CityFAQ, faqSchema } from "./CityFAQ";

const SITE = "https://kennethcarsoninsurance.com";
const COMPARION_URL = "https://www.comparioninsurance.com/insurance-agent/california/elk-grove-0488/kenneth-carson";

const hubSchema = (r) => ({
  "@context": "https://schema.org",
  "@type": "InsuranceAgency",
  "@id": `${SITE}${r.basePath}#agency`,
  "parentOrganization": { "@id": `${SITE}/#organization` },
  "name": `Kenneth Carson - ${r.stateName} Life Insurance Agent`,
  "url": `${SITE}${r.basePath}`,
  "sameAs": [COMPARION_URL],
  "telephone": "+1-916-505-1000",
  "areaServed": [{ "@type": "State", "name": r.stateName }, ...r.cities.map((c) => ({ "@type": "City", "name": `${c.name}, ${r.abbr}` }))],
  "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "74" }
});

const CityCard = ({ c, r, span }) => (
  <Link to={`${r.basePath}/${c.slug}`} className={`group relative h-72 rounded-3xl overflow-hidden ${span}`} data-testid={`${r.key}-hub-city-${c.slug}`}>
    <img src={c.image} alt={`${c.name}, ${r.stateName}`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#143628]/90 to-transparent" />
    <div className="absolute bottom-0 p-6">
      <span className="font-manrope text-xs uppercase tracking-[0.2em] text-white/70">{c.area}</span>
      <h3 className="font-outfit font-bold text-2xl text-white mt-1">Life Insurance Agent {c.name}</h3>
      <span className="inline-flex items-center gap-1 text-[#F0B8A8] font-manrope text-sm mt-2">
        View {c.name} page <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </span>
    </div>
  </Link>
);

const StateHub = ({ region: r, renderContactForm }) => {
  usePageMeta(r.hubMetaTitle, r.hubMetaDescription);

  return (
    <div data-testid={`${r.key}-hub-page`}>
      <script type="application/ld+json">{JSON.stringify(hubSchema(r))}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema(r.hubFaqs))}</script>
      <section className="relative min-h-[520px] flex items-end overflow-hidden">
        <img src={r.hubImage} alt={r.hubImageAlt} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#143628] via-[#143628]/70 to-[#143628]/20" />
        <div className="relative max-w-7xl w-full mx-auto px-6 sm:px-8 lg:px-12 pb-16 pt-32">
          <span className="inline-flex flex-wrap max-w-full items-center gap-2 bg-white/15 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-sm font-manrope mb-5">
            <MapPin className="w-4 h-4" /> {r.hubBadge}
          </span>
          <h1 className="font-outfit font-[850] text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05] max-w-4xl" data-testid={`${r.key}-hub-h1`}>
            Life Insurance Agent Near Me in {r.stateName}
          </h1>
          <p className="font-manrope text-white/85 text-lg mt-6 max-w-2xl">{r.hubIntro}</p>
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <a href={COMPARION_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#C45A41] text-white px-8 py-4 rounded-full font-manrope font-semibold hover:bg-[#A84D37] transition-colors duration-300" data-testid={`${r.key}-hub-comparion-btn`}>
              View Kenneth's Comparion Profile <ChevronRight className="w-5 h-5" />
            </a>
            <a href="tel:+19165051000" className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full font-manrope font-semibold hover:bg-white/20 transition-colors duration-300" data-testid={`${r.key}-hub-call-btn`}>
              <Phone className="w-5 h-5" /> Call (916) 505-1000
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F7F7F5]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-outfit font-[850] text-3xl sm:text-4xl text-[#143628] tracking-tight mb-10">Choose your area</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {r.cities.map((c, i) => {
              const n = r.cities.length;
              const span = i === 0 && n % 3 === 2 ? "lg:col-span-2" : i === n - 1 && n % 3 === 1 ? "sm:col-span-2 lg:col-span-3" : "";
              return <CityCard key={c.slug} c={c} r={r} span={span} />;
            })}
          </div>
        </div>
      </section>

      <CityFAQ faqs={r.hubFaqs} title={`${r.stateName} Life Insurance FAQs`} prefix={r.key} />
      {renderContactForm(r.stateName)}
    </div>
  );
};

export default StateHub;
